import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {AuditActivityId, AuditAutomationRunId, AuditId, TestObjectId, ProjectId, SpecId} from '@webmate-web-core/shared/types/ids';
import {ActivityExecutionStatus, AssessmentEvaluationStatus, AssessmentTask, AuditActivity, HydratedAuditTarget, AuditService} from '@webmate-web-core/shared/services/audit.service';
import {
    faBan,
    faCheckCircle,
    faExternalLink,
    faFlagCheckered,
    faForward,
    faHandPaper,
    faHourglass,
    faMinus,
    faPlus,
    faQuestionCircle,
    faRobot,
    faRunning,
    faSyncAlt,
    faTasks,
    faTimes,
    faTimesCircle,
    faUserCheck
} from '@fortawesome/pro-solid-svg-icons';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {BrowserSessionStateArtifact, State} from '@webmate-web-core/shared/types/browsersession';
import {AuditRequirement} from '@webmate-web-core/shared/tools/audit/audittemplate/shared';
import {Spec, FindingsStats} from '@webmate-web-core/shared/types/testmgmt';
import {switchMap, tap} from 'rxjs/operators';
import {alphanumericDisplayIdSort} from '@webmate-web-core/shared/services/specs';
import {Subject} from 'rxjs';
import {AuditActivityUpdate} from '@webmate-web-core/shared/types/audit';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {MenuItem} from 'primeng/api';
import {Tag, tagToString} from '@webmate-web-core/shared/types/tag';
import {StartAuditAutomationToolComponent, StartAuditAutomationToolConfig} from '@webmate-web-core/shared/tools/audit/automation/start-auditautomation-tool.component';
import {AuditInfo} from '../../../../../workbench/shared/services/audit-session';

@Component({
    selector: 'wm-auditplanning',
    templateUrl: './audit-planning.component.html',
    styleUrls: ['./audit-planning.component.less'],
})
export class AuditPlanningComponent implements OnInit, OnChanges {


    @Input() auditId: AuditId;
    @Input() readonly = false;
    @Input() headerText?: string;
    @Input() projectId: ProjectId;

    // if true, the component will only allow selecting planned activities, not creating new ones, nor modifying the planning matrix, nor doing batch updates
    @Input() selectPlannedActivitiesMode: boolean = false

    @Input() preSelectedActivities?: AuditActivityId[];
    @Input() onlySelectOne?: boolean = false;

    @Output() planningMatrixChange = new EventEmitter<{ [testObjectId: string]: SpecId[] }>();

    @Output() selectionConfirmed = new EventEmitter< { [specId: string]: HydratedAuditTarget[] }>();

    @Output() activitySelected = new EventEmitter<readonly [SpecId, TestObjectId]>();

    @Output() goToActivity = new EventEmitter<{specId: SpecId; activityId: AuditActivityId}>();

    loading = false;
    editMode = false;

    auditInfo?: AuditInfo;

    matrix: { [testObjectId: string]: { [specId: string]: boolean } } = {};

    auditRequirements: AuditRequirement[] = [];
    testObjects: HydratedAuditTarget[] = [];
    testObjectsById: { [testObjectId: string]: HydratedAuditTarget } = {};
    statesByTestObjectId: { [testObjectId: string]: BrowserSessionStateArtifact } = {};

    specs: { [specId: string]: Spec } = {};

    actionsSelectorOptions = [
        {label: 'Plan audit', value: 'plan'},
        {label: 'Batch update', value: 'batch'},
    ];

    selectedActionsSelector: 'plan' | 'batch' | 'select' = 'plan';

    specLabelsMenu: MenuItem[];


    // export type ActivityExecutionStatus = 'notstarted' | 'inprogress' | 'finished' | 'skipped' | 'blocked' | 'waitingforreview';
    executionStatuses: MenuItem[] = [
        {label: 'Not Started', value: 'notstarted'},
        {label: 'In Progress', value: 'inprogress'},
        {label: 'Finished', value: 'finished'},
        {label: 'Skipped', value: 'skipped'},
        {label: 'Blocked', value: 'blocked'},
        {label: 'Waiting for Review', value: 'waitingforreview'},
    ];
    executionStatusMenu = this.executionStatuses.map(es => {
        return {
            label: es.label,
            command: () => {
                this.applyExecutionStatusSelection(es.value);
            }
        };
    })

    evaluationStatuses: MenuItem[] = [
        {label: 'Passed', value: 'passed'},
        {label: 'Failed', value: 'failed'},
        {label: 'Undecided', value: 'undecided'},
        {label: 'Not Applicable', value: 'notapplicable'},
    ];
    evaluationStatusMenu = this.evaluationStatuses.map(es => {
        return {
            label: es.label,
            command: () => {
                this.applyEvaluationStatusSelection(es.value);
            }
        };
    })

    sortedSpecIdsAccordingToDisplayId: SpecId[] = [];

    findingStatsBySpecAndTestObject: { [specId: string]: { [testObjectId: string]: FindingsStats } } = {};

    activitiesBySpecAndTestObject: { [specId: string]: { [testObjectId: string]: AuditActivity } } = {};

    selectedActivitiesBySpecAndTestObject: { [specId: string]: { [testObjectId: string]: boolean } } = {};

    wantRefresh$ = new Subject<void>();

    get selectedActivitiesCount(): number {
        return Object.values(this.selectedActivitiesBySpecAndTestObject)
            .reduce((sum, testObjectsMap) =>
                    sum + Object.values(testObjectsMap).filter(v => v).length
                , 0);
    }


    constructor(private auditService: AuditService,
                private testService: TestService,
                private toolService: ModalToolContainerService,
                private notificationService: WmNotificationService,
                public hostElement: ElementRef<HTMLElement>) {
        this.loading = true;
    }

    ngOnInit() {
        if (this.selectPlannedActivitiesMode) {
            this.selectedActionsSelector = 'select';
        }

        this.wantRefresh$.pipe(
            tap(() => {
                this.loading = true;
            }),
            switchMap(() => this.auditService.getOrCreateAuditSession(this.auditId).getAuditInfo$())
        ).subscribe(auditInfo => {
            this.auditInfo = auditInfo;
            const testObjects = auditInfo.testObjects || [];
            const auditRequirements = auditInfo.auditRequirements || [];

            this.specs = auditInfo.specs || {};
            this.matrix = Object.entries(auditInfo.audit?.workflow.spec.planningMatrix || {}).reduce((acc, [testObjectId, specIds]) => {
                acc[testObjectId] = {};
                specIds.forEach(specId => {
                    acc[testObjectId][specId] = true;
                });
                return acc;
            }, {} as any);

            this.activitiesBySpecAndTestObject = auditInfo.assessmentTasks?.reduce((acc, activity) => {
                const specId = activity.assessedSpec;
                const testObjectId = this.getTaskTargetId(activity);
                if (!testObjectId) {
                    return acc;
                }
                if (!acc[specId]) {
                    acc[specId] = {};
                }
                acc[specId][testObjectId] = activity;
                return acc;
            }, {} as any);

            this.testService.getFindingsStatsForAudit$(this.projectId, this.auditId).subscribe(stats => {
                this.findingStatsBySpecAndTestObject = stats || {};
            });

            this.testObjectsById = testObjects.reduce((acc, testObject) => {
                acc[testObject.testObjectId] = testObject;
                return acc;
            }, {} as any);

            this.sortedSpecIdsAccordingToDisplayId = Object.keys(this.specs).sort((a, b) => {
                return alphanumericDisplayIdSort(this.specs[a]?.displayId || '', this.specs[b]?.displayId || '');
            });

            this.statesByTestObjectId = auditInfo.statesByTestObjectId || {};
            this.testObjects = testObjects;
            this.auditRequirements = auditRequirements;

            const labels = Object.values(auditInfo.specs || {}).flatMap(spec => spec.labels).filter(label => label !== undefined) as Tag[];
            const labelsByIds = labels.reduce((acc, label) => {
                const labelId = tagToString(label);
                if (!acc[labelId]) {
                    acc[labelId] = label;
                }
                return acc;
            }, {} as { [labelId: string]: Tag });

            this.specLabelsMenu = Object.entries(labelsByIds).map(([labelId, tag]) => ({
                label: tagToString(tag),
                value: tag,
            })).sort((a, b) => {
                return a.label.localeCompare(b.label);
            });

            if (this.preSelectedActivities) {
                this.preSelectedActivities.forEach(activityId => {
                    Object.entries(this.activitiesBySpecAndTestObject).forEach(([specId, testObjects]) => {
                        Object.entries(testObjects).forEach(([testObjectId, activity]) => {
                            if (activity.id === activityId) {
                                if (!this.selectedActivitiesBySpecAndTestObject[specId]) {
                                    this.selectedActivitiesBySpecAndTestObject[specId] = {};
                                }
                                this.selectedActivitiesBySpecAndTestObject[specId][testObjectId] = true;
                            }
                        });
                    });
                });
            }

            this.loading = !auditInfo.audit;
        });

        this.wantRefresh$.next();
    }

    ngOnChanges() {
        this.wantRefresh$.next();
    }

    clearSelection() {
        this.selectedActivitiesBySpecAndTestObject = {};
    }

    toggleCellSelection(testObjectId: TestObjectId, specId: SpecId) {
        if (this.onlySelectOne) {
            this.selectionConfirmed.emit({[specId]: [this.testObjectsById[testObjectId]]});
            return;
        }

        if (this.selectedActionsSelector === 'plan' || this.matrix[testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) {
            this.selectedActivitiesBySpecAndTestObject[specId] = this.selectedActivitiesBySpecAndTestObject[specId] || {};
            this.selectedActivitiesBySpecAndTestObject[specId][testObjectId] = !(this.selectedActivitiesBySpecAndTestObject[specId][testObjectId]);
        }
    }

    selectAll() {
        this.clearSelection();
        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            this.selectedActivitiesBySpecAndTestObject[specId] = {};
            this.testObjects.forEach(testObject => {
                if (this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) {
                    this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = true;
                }
            });
        });
    }

    applyLabelsSelection(selectedLabel: Tag) {
        const labelId = tagToString(selectedLabel);
        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            this.selectedActivitiesBySpecAndTestObject[specId] ||= {};
            const spec = this.specs[specId];
            this.testObjects.forEach(testObject => {
                if (this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) {
                    if (spec.labels && spec.labels.some(label => {
                        return tagToString(label) === labelId;
                    })) {
                        this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = true;
                    }
                }
            });
        });
    }

    applyExecutionStatusSelection(status: ActivityExecutionStatus) {
        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            this.selectedActivitiesBySpecAndTestObject[specId] ||= {};
            this.testObjects.forEach(testObject => {
                if (this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) {
                    const activity = this.activitiesBySpecAndTestObject[specId]?.[testObject.testObjectId];
                    if (activity && status === activity.executionStatus) {
                        this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = true;
                    }
                }
            });
        });
    }

    applyEvaluationStatusSelection(status: AssessmentEvaluationStatus) {
        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            this.selectedActivitiesBySpecAndTestObject[specId] ||= {};
            this.testObjects.forEach(testObject => {
                if (this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) {
                    const activity = this.activitiesBySpecAndTestObject[specId]?.[testObject.testObjectId];
                    if (activity && status === activity.evaluationStatus) {
                        this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = true;
                    }
                }
            });
        });
    }

    selectNone() {
        this.clearSelection();
    }

    invertSelection() {
        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            this.testObjects.forEach(testObject => {
                if (this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) {
                    if (!this.selectedActivitiesBySpecAndTestObject[specId]) {
                        this.selectedActivitiesBySpecAndTestObject[specId] = {};
                    }
                    this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = !this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId];
                }
            });
        });
    }

    selectByAutomatable() {
        // For the time being, we naively assume that an activity is automatable if
        //   a) there is at least one SpecCheckerConfig (checkers) in a Spec and
        //   b) the test object has a target of type StateObservationTarget
        //
        // In the future, the decision may be more nuanced. For instance, there could also be non-automated checkers (think: a configuration for a manual workflow)
        // and there could be some explicit flag if a test object is automatable,
        // or even if a checker is compatible with a test object
        // (e.g. if the test object is specifically on firefox, but the checker depends on CDP)

        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            const spec = this.specs[specId];

            if (!spec ||  !spec.checkers || spec.checkers.length === 0) {
                return;
            }

            this.selectedActivitiesBySpecAndTestObject[specId] = {};
            this.testObjects.forEach(testObject => {
                if ((this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) && testObject.target.type === "StateObservationTarget") {
                    this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = true;
                }
            });
        });
    }

    selectByTestObject(testObjectId: TestObjectId, event: MouseEvent) {
        this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
            if (!event.shiftKey) {
                this.selectedActivitiesBySpecAndTestObject[specId] = {};
            }
            this.testObjects.forEach(testObject => {
                if ((this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[specId] /* in batch mode, only matrix elements are allowed */) && testObject.testObjectId === testObjectId) {
                    this.selectedActivitiesBySpecAndTestObject[specId][testObject.testObjectId] = true;
                }
            });
        });
        event.stopPropagation();
        event.preventDefault();
    }

    selectBySpec(selectedSpec: SpecId, event: MouseEvent) {
        if (!event.shiftKey) {
            this.sortedSpecIdsAccordingToDisplayId.forEach(specId => {
                this.selectedActivitiesBySpecAndTestObject[specId] = {};
            });
        }

        this.testObjects.forEach(testObject => {
            if (((this.selectedActionsSelector === 'plan' || this.matrix[testObject.testObjectId]?.[selectedSpec]) /* in batch mode, only matrix elements are allowed */) /*&& specId === selectedSpec*/) {
                this.selectedActivitiesBySpecAndTestObject[selectedSpec][testObject.testObjectId] = true;
            }
        });

        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
    }

    batchUpdateActivities(activities: AssessmentTask[], updateActions: AuditActivityUpdate[]) {
        this.loading = true;
        this.auditService.batchUpdateAuditActivities$(this.auditId, activities.map(a => a.id), updateActions).subscribe({
            next: () => {
                this.wantRefresh$.next();
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    setExecutionStatus(status: ActivityExecutionStatus) {
        const activities = Object.entries(this.selectedActivitiesBySpecAndTestObject).reduce((acc, [specId, testObjects]) => {
            Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                if (selected && !!this.activitiesBySpecAndTestObject[specId]?.[testObjectId]) {
                    acc.push(this.activitiesBySpecAndTestObject[specId]?.[testObjectId]);
                }
            });
            return acc;
        }, [] as AuditActivity[]);

        this.notificationService.open(`Are you sure you want to set the execution status of ${activities.length} activities to ${status}?`, 'Yes, set status', () => {
            this.batchUpdateActivities(activities, [{
                type: 'SetAuditActivityExecutionStatus',
                executionStatus: status
            }]);
        }, 'No, cancel', () => {
            // Do nothing
        });
    }

    startAutomation() {
        const activities = Object.entries(this.selectedActivitiesBySpecAndTestObject).reduce((acc, [specId, testObjects]) => {
            Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                // only add to automation if the activity is selected and the related test object is not a custom object
                if (selected && this.testObjectsById[testObjectId].target.type !== "TagObservationTarget" && !!this.activitiesBySpecAndTestObject[specId]?.[testObjectId]) {
                    acc.push(this.activitiesBySpecAndTestObject[specId]?.[testObjectId]);
                }
            });
            return acc;
        }, [] as AuditActivity[]);

        this.toolService.openTool(StartAuditAutomationToolComponent, new StartAuditAutomationToolConfig(this.projectId, this.auditId, this.auditInfo.audit.reportParameters ?? {},
            activities.map(act => act.id), (run: AuditAutomationRunId) => {
            this.wantRefresh$.next();
        }));
    }

    setEvaluationStatus(status: AssessmentEvaluationStatus) {
        const activities = Object.entries(this.selectedActivitiesBySpecAndTestObject).reduce((acc, [specId, testObjects]) => {
            Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                if (selected && !!this.activitiesBySpecAndTestObject[specId]?.[testObjectId]) {
                    acc.push(this.activitiesBySpecAndTestObject[specId]?.[testObjectId]);
                }
            });
            return acc;
        }, [] as AuditActivity[]);

        this.notificationService.open(`Are you sure you want to set the evaluation status of ${activities.length} activities to ${status}?`, 'Yes, set status', () => {
            this.batchUpdateActivities(activities, [{
                type: 'SetAssessmentTaskEvaluationStatus',
                evaluationStatus: status
            }]);
        }, 'No, cancel', () => {
            // Do nothing
        });
    }


    actionsSelectorChanged() {
        // if we switch from plan to batch mode, we need to unset all activities, which are not planned
        if (this.selectedActionsSelector === 'batch') {
            Object.entries(this.selectedActivitiesBySpecAndTestObject).forEach(([specId, testObjects]) => {
                Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                    if (!this.matrix[testObjectId]?.[specId]) {
                        delete this.selectedActivitiesBySpecAndTestObject[specId][testObjectId];
                    }
                });
            });
        }
    }

    addSelectedActivitiesToPlanningMatrix() {
        Object.entries(this.selectedActivitiesBySpecAndTestObject).forEach(([specId, testObjects]) => {
            Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                if (selected) {
                    if (!this.matrix[testObjectId]) {
                        this.matrix[testObjectId] = {};
                    }
                    this.matrix[testObjectId][specId] = true;
                }
            });
        });

        this.emitPlanningMatrix();
    }

    removeSelectedActivitiesFromPlanningMatrix() {
        Object.entries(this.selectedActivitiesBySpecAndTestObject).forEach(([specId, testObjects]) => {
            Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                if (selected) {
                    if (this.matrix[testObjectId]) {
                        delete this.matrix[testObjectId][specId];
                    }
                }
            });
        });

        this.emitPlanningMatrix();
    }

    emitPlanningMatrix() {
        const planningMatrix: { [testObjectId: string]: SpecId[] } = {};
        Object.entries(this.matrix).forEach(([testObjectId, specIds]) => {
            planningMatrix[testObjectId] = Object.keys(specIds);
        });
        this.planningMatrixChange.emit(planningMatrix);
        this.selectedActivitiesBySpecAndTestObject = {};
    }

    confirmSelection() {
        const result = Object.entries(this.selectedActivitiesBySpecAndTestObject).reduce((acc, [specId, testObjects]) => {
            Object.entries(testObjects).forEach(([testObjectId, selected]) => {
                if (selected) {
                    if (!acc[specId]) {
                        acc[specId] = [];
                    }
                    acc[specId].push(this.testObjectsById[testObjectId]);
                }
            });
            return acc;
        }, {} as { [specId: string]: HydratedAuditTarget[] });
        this.selectionConfirmed.emit(result);
    }


    goToActivityDetails(specId: SpecId, testObjectId: TestObjectId) {
        const activityId = AuditService.generateFlyweightActivityId(testObjectId, specId);
        if (activityId) {
            this.goToActivity.emit({specId, activityId});
        }
    }

    protected readonly faPlus = faPlus;
    protected readonly faTimes = faTimes;
    protected readonly faUserCheck = faUserCheck;
    protected readonly faRunning = faRunning;
    protected readonly faForward = faForward;
    protected readonly faTasks = faTasks;
    protected readonly faFlagCheckered = faFlagCheckered;
    protected readonly faHandPaper = faHandPaper;
    protected readonly faQuestionCircle = faQuestionCircle;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faTimesCircle = faTimesCircle;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faBan = faBan;
    protected readonly faMinus = faMinus;
    protected readonly faRobot = faRobot;
    protected readonly faHourglass = faHourglass;
    protected readonly faExternalLink = faExternalLink;

    private getTaskTargetId(activity: AuditActivity): TestObjectId | undefined {
        return (activity as AssessmentTask).testObjectId;
    }
}
